import * as React from 'react';

import PropTypes from 'prop-types';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import type { PropsFromWithProductFeatures } from 'bundles/admin-dashboard/components/AdminDashboardPage';
import type { EnterpriseAdminRole, ThirdPartyOrganization } from 'bundles/admin-dashboard/types';
import { handleRedirect } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/utils';
import type { EnterpriseContractsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseContractsV1';

const CourseraForCampusBasicSetupContainer = createLoadableComponent(
  () => import('bundles/enterprise-admin-coursera-for-campus-basic/components/CourseraForCampusBasicSetupContainer')
);
const ProgramSurveyContainer = createLoadableComponent(
  () => import('bundles/enterprise-admin-coursera-for-campus-basic/components/ProgramSurveyContainer')
);
const InviteLearnersFlowContainer = createLoadableComponent(
  () => import('bundles/enterprise-admin-coursera-for-campus-basic/components/InviteLearnersFlowContainer')
);

export const COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH = 'coursera-for-campus-basic-setup';
export const FREEMIUM_PROGRAM_SETUP_PATH = 'freemium-program-setup';
export const FREEMIUM_INVITE_LEARNERS_PATH = 'freemium-invite-learners';
export const FREEMIUM_PROGRAM_SURVEY_PATH = 'freemium-program-survey';

type SecondaryOrgTypeProps = Partial<Pick<PropsFromWithProductFeatures, 'isC4CBOrg' | 'isGwGOrg' | 'isGwGFreemiumOrg'>>;

export type Props = {
  contracts: Array<EnterpriseContractsV1>;
  thirdPartyOrganization: ThirdPartyOrganization;
  enterpriseAdminRoles: Array<EnterpriseAdminRole>;
} & SecondaryOrgTypeProps;

const withCourseraForCampusBasicSetup = (Component: React.ComponentType) =>
  class CourseraForCampusBasicSetup extends React.Component<Props> {
    static contextTypes = {
      router: PropTypes.object.isRequired,
    };

    render() {
      const { router } = this.context;
      const { thirdPartySlug, programSlug } = router.params;
      const { contracts = [], thirdPartyOrganization, isC4CBOrg } = this.props;
      const currentPath = router.routes[router.routes.length - 1].path;
      const isCourseraForCampusBasicSetupPath = currentPath === COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH;
      const isFreemiumProgramSetupPath = currentPath === FREEMIUM_PROGRAM_SETUP_PATH;
      const isFreemiumInviteLearnersPath = currentPath === FREEMIUM_INVITE_LEARNERS_PATH;
      const isProgramSurveyPath = currentPath === FREEMIUM_PROGRAM_SURVEY_PATH;

      if (
        isCourseraForCampusBasicSetupPath ||
        isFreemiumProgramSetupPath ||
        isFreemiumInviteLearnersPath ||
        isProgramSurveyPath
      ) {
        if (!isC4CBOrg) {
          handleRedirect(`/o/${thirdPartySlug}/admin/home`, router);
          return null;
        }

        if (isFreemiumProgramSetupPath) {
          return (
            <CourseraForCampusBasicSetupContainer
              contracts={contracts}
              orgSlug={thirdPartySlug}
              orgId={thirdPartyOrganization.id}
              programSlug={programSlug}
              router={router}
              isProgramCreation
            />
          );
        }

        if (isCourseraForCampusBasicSetupPath) {
          return (
            <CourseraForCampusBasicSetupContainer
              contracts={contracts}
              orgSlug={thirdPartySlug}
              orgId={thirdPartyOrganization.id}
              router={router}
              programSlug={programSlug}
            />
          );
        }

        if (isFreemiumInviteLearnersPath) {
          return <InviteLearnersFlowContainer orgSlug={thirdPartySlug} orgId={thirdPartyOrganization.id} />;
        }

        if (isProgramSurveyPath) {
          return <ProgramSurveyContainer />;
        }
      }

      return <Component {...this.props} />;
    }
  };

export default withCourseraForCampusBasicSetup;
